import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "sonner";
import LandingPage from "@/components/LandingPage";
import Login from "@/components/Login";
import UserLogin from "@/components/UserLogin";
import Register from "@/components/Register";
import ForexCalculator from "@/components/ForexCalculator";
import TradeHistory from "@/components/TradeHistory";
import MT5Connection from "@/components/MT5Connection";
import BusinessDashboard from "@/components/BusinessDashboard";
import OnboardingDashboard from "@/components/OnboardingDashboard";
import UserProfileDashboard from "@/components/UserProfileDashboard";
import TradeHistoryDashboard from "@/components/TradeHistoryDashboard";
import RequestInvite from "@/components/RequestInvite";
import AdminDashboard from "@/components/AdminDashboard";
import ForgotPassword from "@/components/ForgotPassword";
import ResetPassword from "@/components/ResetPassword";
import UpgradeSubscription from "@/components/UpgradeSubscription";
import PaymentSuccess from "@/components/PaymentSuccess";
import { BACKEND_URL } from "@/config";
import { FamilyAccessGate } from "@/components/family/FamilyAccessGate";
import TradeScreen from "@/components/sim/TradeScreen";
import { rememberRedirect, getRedirect } from "@/components/sim/authRedirect";

const LoginFirst = () => {
  const location = useLocation();
  rememberRedirect(location.pathname + location.search);
  return <Navigate to="/login" replace />;
};

const API = `${BACKEND_URL}/api`;

// Setup axios defaults with auth token
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("auth_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

// Handle 401 errors globally — but NEVER hijack the auth endpoints themselves.
// A failed login/verify/logout must surface its error to the form (so the user
// sees "Invalid email or password"), not silently wipe storage and bounce to "/"
// which looks exactly like "I can't log in / nothing happens".
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const url = error.config?.url || "";
    const isAuthEndpoint =
      url.includes("/login") ||
      url.includes("/logout") ||
      url.includes("/verify-session");
    if (error.response?.status === 401 && !isAuthEndpoint) {
      localStorage.removeItem("auth_token");
      localStorage.removeItem("user_email");
      localStorage.removeItem("user_role");
      window.location.href = "/";
    }
    return Promise.reject(error);
  }
);

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [mt5Connected, setMt5Connected] = useState(false);
  const [mt5Account, setMt5Account] = useState(null);
  
  // 🔒 URL Safety Check - Warn if somehow wrong URL is being used
  const [urlWarning, setUrlWarning] = useState(null);
  
  useEffect(() => {
    // Log backend URL being used (for debugging)
    console.log('✅ Backend URL:', BACKEND_URL);
  }, []);

  useEffect(() => {
    // Check if user has valid session on mount
    const verifySession = async () => {
      const token = localStorage.getItem("auth_token");
      const role = localStorage.getItem("user_role") || 'user';
      if (token) {
        try {
          await axios.get(`${API}/verify-session`);
          setIsAuthenticated(true);
          setUserRole(role);
        } catch (err) {
          // Only invalidate the session on a GENUINE auth failure (401/403).
          // Transient errors (network drop, timeout, 5xx, Cloudflare HTML error
          // pages) must NOT log the user out — otherwise a brief backend hiccup
          // boots them on refresh. Trust the stored token and stay logged in.
          const status = err.response?.status;
          if (status === 401 || status === 403) {
            localStorage.removeItem("auth_token");
            localStorage.removeItem("user_email");
            localStorage.removeItem("user_role");
            setIsAuthenticated(false);
            setUserRole(null);
          } else {
            setIsAuthenticated(true);
            setUserRole(role);
          }
        }
      }
      setIsLoading(false);
    };

    verifySession();
  }, []);

  const handleLoginSuccess = (token) => {
    const role = localStorage.getItem("user_role") || 'user';
    setIsAuthenticated(true);
    setUserRole(role);
  };

  const handleLogout = async () => {
    // Check if user is admin before clearing storage
    const isAdminUser = userRole === 'admin';
    
    try {
      await axios.post(`${API}/logout`);
    } catch (err) {
      console.error("Logout error:", err);
    }
    localStorage.removeItem("auth_token");
    localStorage.removeItem("user_email");
    localStorage.removeItem("user_role");
    setIsAuthenticated(false);
    setUserRole(null);
    setMt5Connected(false);
    setMt5Account(null);
    
    // Redirect to appropriate login page
    if (isAdminUser) {
      window.location.href = '/kalebadmincelovic';
    } else {
      window.location.href = '/login';
    }
  };

  // Helper function to check if user is admin
  const isAdmin = () => {
    return userRole === 'admin' || userRole === 'owner';
  };

  if (isLoading) {
    return (
      <div style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}>
        <div style={{ color: "#0891b2", fontSize: "1.25rem", fontWeight: "600" }}>
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      {/* 🚨 URL Warning Banner - Only shows if wrong URL detected */}
      {urlWarning && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          background: '#dc2626',
          color: 'white',
          padding: '10px 20px',
          textAlign: 'center',
          fontWeight: 'bold',
          zIndex: 99999,
          fontSize: '14px'
        }}>
          🚨 CONFIGURATION ERROR: {urlWarning} - Please refresh or contact support
        </div>
      )}
      <BrowserRouter>
        <FamilyAccessGate enabled={isAuthenticated && !isAdmin()} onLogout={handleLogout}>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/register" element={<Register />} />
          <Route path="/request-invite" element={<RequestInvite />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          
          {/* User Login Route (Light Purple) */}
          <Route 
            path="/login" 
            element={
              isAuthenticated ? 
                <Navigate to={getRedirect()} replace /> : 
                <UserLogin onLoginSuccess={handleLoginSuccess} />
            } 
          />
          
          {/* Admin Login Route (Hidden) */}
          <Route 
            path="/kalebadmincelovic" 
            element={
              isAuthenticated && isAdmin() ? 
                <Navigate to="/admin-dashboard" replace /> : 
                isAuthenticated ?
                <Navigate to="/calculator" replace /> :
                <Login onLoginSuccess={handleLoginSuccess} />
            } 
          />

          {/* Protected Routes - Require Authentication */}
          <Route
            path="/calculator"
            element={
              isAuthenticated ? (
                <TradeScreen onLogout={handleLogout} isAdmin={isAdmin()} />
              ) : (
                <LoginFirst />
              )
            }
          />
          <Route
            path="/calculator-classic"
            element={
              isAuthenticated && isAdmin() ? (
                <ForexCalculator
                  mt5Connected={mt5Connected}
                  mt5Account={mt5Account}
                  onLogout={handleLogout}
                />
              ) : (
                <Navigate to="/calculator" replace />
              )
            }
          />
          <Route 
            path="/history" 
            element={
              isAuthenticated ? 
                <TradeHistory onLogout={handleLogout} /> : 
                <Navigate to="/login" replace />
            } 
          />
          <Route
            path="/mt5"
            element={
              isAuthenticated ? (
                <MT5Connection
                  setMt5Connected={setMt5Connected}
                  setMt5Account={setMt5Account}
                  onLogout={handleLogout}
                />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route 
            path="/business" 
            element={
              isAuthenticated && isAdmin() ? 
                <BusinessDashboard /> : 
                isAuthenticated ?
                  <Navigate to="/calculator" replace /> :
                  <Navigate to="/login" replace />
            } 
          />
          <Route 
            path="/onboarding" 
            element={
              isAuthenticated ? 
                <OnboardingDashboard /> : 
                <Navigate to="/login" replace />
            } 
          />
          <Route 
            path="/profile" 
            element={
              isAuthenticated ? 
                <UserProfileDashboard /> : 
                <Navigate to="/login" replace />
            } 
          />
          <Route 
            path="/trade-stats" 
            element={
              isAuthenticated ? 
                <TradeHistoryDashboard /> : 
                <Navigate to="/login" replace />
            } 
          />
          <Route 
            path="/upgrade" 
            element={
              isAuthenticated ? 
                <UpgradeSubscription /> : 
                <Navigate to="/login" replace />
            } 
          />
          <Route 
            path="/payment-success" 
            element={
              isAuthenticated ? 
                <PaymentSuccess /> : 
                <Navigate to="/login" replace />
            } 
          />
          <Route 
            path="/admin-dashboard" 
            element={
              isAuthenticated && isAdmin() ? 
                <AdminDashboard /> : 
                isAuthenticated ?
                  <Navigate to="/calculator" replace /> :
                  <Navigate to="/login" replace />
            } 
          />
          
          {/* Catch all - redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </FamilyAccessGate>
      </BrowserRouter>
    </div>
  );
}

export default App;